import { useEffect, useRef, useState } from "react";
import { WebAuthnAbortService, browserSupportsWebAuthn, startAuthentication } from "@simplewebauthn/browser";
import { api, type User } from "../lib/api.ts";
import { createPasskeyLogin, type PasskeyLogin, type PasskeyPhase } from "../lib/passkeyLogin.ts";

export const passkeyButtonLabel = (phase: PasskeyPhase): string =>
  phase === "idle"
    ? "Sign in with passkey"
    : phase === "verifying"
      ? "Signing in…"
      : phase === "preparing"
        ? "Preparing passkey…"
        : "Waiting for passkey…";

/** Production wiring of the passkey controller (SimpleWebAuthn + the racler.dev API). */
export function createBrowserPasskeyLogin(onPhase: (p: PasskeyPhase) => void): PasskeyLogin {
  return createPasskeyLogin({
    fetchOptions: () => api.loginOptions(),
    startAuthentication: (args) => startAuthentication(args),
    verify: (ceremonyId, credential) => api.loginVerify(ceremonyId, credential),
    cancelCeremony: () => WebAuthnAbortService.cancelCeremony(),
    supportsWebAuthn: () => browserSupportsWebAuthn(),
    onPhase,
  });
}

export function LoginView({
  phase,
  error,
  onSignIn,
  onCancel,
}: {
  phase: PasskeyPhase;
  error: string | null;
  onSignIn: () => void;
  onCancel: () => void;
}) {
  const busy = phase !== "idle";
  return (
    <div className="center">
      <div className="card">
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "16px" }}>
          <img src="/logo.png" alt="racler.dev" width="64" height="64" style={{ borderRadius: "14px" }} />
        </div>
        <h1>racler.dev</h1>
        <p className="muted">Sign in with your passkey.</p>
        <button className="primary" disabled={busy} onClick={onSignIn} data-testid="login" aria-busy={busy}>
          {passkeyButtonLabel(phase)}
        </button>
        {phase === "waiting" || phase === "preparing" ? (
          <button type="button" onClick={onCancel} data-testid="login-cancel" style={{ marginLeft: 8 }}>
            Cancel
          </button>
        ) : null}
        {error && (
          <p className="error" role="alert" data-testid="login-error">
            {error}
          </p>
        )}
        <p className="muted small">
          Works with Touch ID, Face ID, Windows Hello, Android, password managers, security keys or a phone
          nearby. Registration is invite-only.
        </p>
      </div>
    </div>
  );
}

export function LoginPage({ onLogin }: { onLogin: (u: User) => void }) {
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<PasskeyPhase>("idle");
  const loginRef = useRef<PasskeyLogin | null>(null);
  loginRef.current ??= createBrowserPasskeyLogin(setPhase);

  // Fetch a challenge ahead of the click, so the click can open the passkey prompt immediately.
  useEffect(() => {
    loginRef.current?.prefetch();
    return () => loginRef.current?.cancel();
  }, []);

  const signIn = () => {
    const login = loginRef.current;
    if (!login) return;
    setError(null);
    // Called synchronously from the click handler: WebAuthn starts inside the user gesture.
    void login.signIn().then((result) => {
      if (result.ok) {
        onLogin(result.user);
        return;
      }
      setError(result.failure.message);
      login.prefetch(); // ready for a manual retry (never retried automatically)
    });
  };

  return (
    <LoginView phase={phase} error={error} onSignIn={signIn} onCancel={() => loginRef.current?.cancel()} />
  );
}
